import axios from 'axios';

// In production (Render), use relative URLs since frontend and backend are same origin
// In development, use localhost:8000
const API_BASE_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000, // 10 second timeout
});

// 2026-04-02: Session token interceptors — enforce single active session
api.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('sessionToken');
  const userId = sessionStorage.getItem('userId');
  if (token && userId) {
    config.headers['X-Session-Token'] = token;
    config.headers['X-User-Id'] = userId;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      sessionStorage.removeItem('userId');
      sessionStorage.removeItem('sessionToken');
      window.dispatchEvent(new CustomEvent('session-expired'));
    }
    return Promise.reject(error);
  }
);

// API service object with all your endpoints
export const apiService = {
  // Auth endpoints
  login: async (credentials) => {
    const response = await api.post('/login', credentials);
    return response.data;
  },

  // 2026-06-17: Exchange a WorkOS web access token for a RothGPT session (Phase 3)
  webSession: async (accessToken) => {
    const response = await api.post('/web/session', null, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data;
  },

  // User endpoints
  createUser: async (userData) => {
    const response = await api.post('/users', userData);
    return response.data;
  },

  getUser: async (userId) => {
    const response = await api.get(`/users/${userId}`);
    return response.data;
  },

  updateUserProfile: async (userId, profileData) => {
    const response = await api.put(`/users/${userId}`, profileData);
    return response.data;
  },

  changePassword: async (userId, currentPassword, newPassword) => {
    const response = await api.post(`/users/${userId}/change-password`, {
      current_password: currentPassword,
      new_password: newPassword
    });
    return response.data;
  },

  deleteAccount: async (userId) => {
    const response = await api.delete(`/users/${userId}`);
    return response.data;
  },

  // Input endpoints
  createOrUpdateInputs: async (inputData) => {
    const response = await api.post('/inputs', inputData);
    return response.data;
  },

  getInputs: async (userId) => {
    const response = await api.get(`/inputs/${userId}`);
    return response.data;
  },

  // Calculation endpoints
  calculateYearData: async (userId) => {
    const response = await api.post(`/calculate-yr-data/${userId}`);
    return response.data;
  },


  // 2026-04-03: Combined endpoint — single call replaces 4 separate fetches
  getCalculationResults: async (runId) => {
    const response = await api.get(`/calculation_results/${runId}`);
    return response.data;
  },

  // Data retrieval endpoints (kept for individual use)
  getRothConversions: async (runId) => {
    const response = await api.get(`/roth_conversions/${runId}`);
    return response.data;
  },

  getRothConversionsParts: async (runId) => {
    const response = await api.get(`/roth_conversions_parts/${runId}`);
    return response.data;
  },

  getRetireYearData: async (runId) => {
    const response = await api.get(`/retire_yr_data/${runId}`);
    return response.data;
  },

  getDistributionSchedule: async (runId) => {
    const response = await api.get(`/distribution_schedule/${runId}`);
    return response.data;
  },

  getRetireYearDataComparison: async (runId, convGroupNum) => {
    const response = await api.get(`/retire_yr_data_comparison/${runId}/${convGroupNum}`);
    return response.data;
  },

  // Combined operations
  // 2026-04-02: OLD: POST calc + 3 redundant GET calls (conversions, parts, retireYearData)
  // Data fetching now handled by useConversions hook when runId updates via refetchInputs()
  runFullCalculation: async (userId) => {
    try {
      console.log('Starting full calculation for user:', userId);

      // POST calc only — returns metadata (run_id, calc_count, etc.)
      const yrDataResponse = await apiService.calculateYearData(userId);

      return {
        runId: yrDataResponse.run_id,
        recordsCreated: yrDataResponse.records_created,
        calcCount: yrDataResponse.calc_count,
        subscriptionStatus: yrDataResponse.subscription_status,
        distribution: yrDataResponse.distribution,
        annuity_factor_multiple: yrDataResponse.annuity_factor_multiple,
        base_duration: yrDataResponse.base_duration
      };
    } catch (error) {
      console.error('Full calculation failed:', error);
      throw new Error(`Calculation failed: ${error.response?.data?.detail || error.message}`);
    }
  },

  // Rating endpoints
  submitRating: async (ratingData) => {
    const response = await api.post('/ratings', ratingData);
    return response.data;
  },

  getRatingsSummary: async (userId = null) => {
    const url = userId ? `/ratings/summary?user_id=${userId}` : '/ratings/summary';
    const response = await api.get(url);
    return response.data;
  },

  // Subscription endpoints
  getPriceIds: async () => {
    const response = await api.get('/stripe/price-ids');
    return response.data;
  },

  selectFreePlan: async (userId) => {
    const response = await api.post(`/users/${userId}/select-free`);
    return response.data;
  },

  getSubscriptionStatus: async (userId) => {
    const response = await api.get(`/users/${userId}/subscription-status`);
    return response.data;
  },

  createCheckoutSession: async (userId, priceId) => {
    const response = await api.post('/stripe/create-checkout-session', {
      user_id: userId,
      price_id: priceId
    });
    return response.data;
  },

  // Report endpoints
  downloadReport: async (runId) => {
    const response = await api.get(`/download_report/${runId}`, { responseType: 'blob' });
    return response.data;
  },

  emailReport: async (runId) => {
    const response = await api.post(`/email_report/${runId}`);
    return response.data;
  },

  getMtrGraphData: async (params = {}) => {
    const response = await api.get('/mtr-graph-data', { params });
    return response.data;
  },

  // 2026-03-19: RMD table for tax drag chart
  getRmdTable: async () => {
    const response = await api.get('/rmd-table');
    return response.data;
  },

  // 2026-03-20: Tax drag chart data — single source of truth from backend
  getTaxDragChartData: async (params = {}) => {
    const response = await api.get('/tax-drag-chart-data', { params });
    return response.data;
  }
};

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      data: error.response?.data
    });
    return Promise.reject(error);
  }
);

export default apiService;